'use client';

import { useRef, useEffect } from 'react';
import { varAlpha } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import CardMedia from '@mui/material/CardMedia';
import IconButton from '@mui/material/IconButton';

import { useResponsive } from 'src/hooks/use-responsive';

import { Iconify } from 'src/components/iconify';

import { useVideoStore } from './video-store';

// ----------------------------------------------------------------------

const iconButtonStyles = (theme) => ({
  zIndex: 9,
  position: 'absolute',
  padding: theme.spacing(1),
  color: theme.palette.common.white,
  '&:hover': {
    ...theme.mixins.bgBlur({
      color: varAlpha(theme.vars.palette.grey['900Channel'], 0.48),
    }),
  },
});

export function Video({ src, isVisible, slotProps, sx, ...other }) {
  const videoRef = useRef(null);

  const smDown = useResponsive('down', 'sm');

  const { play, muted, handlePlay, handleMuted } = useVideoStore();

  const handleMouseEnter = (video) => {
    if (play) {
      video.muted = muted;
      video.play();
    }
  };

  const handleMouseLeave = (video) => {
    video.currentTime = 0;
    video.muted = true;
    video.pause();
  };

  useEffect(() => {
    if (smDown && videoRef.current) {
      if (isVisible) {
        handleMouseEnter(videoRef.current);
      } else {
        handleMouseLeave(videoRef.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smDown, isVisible]);

  return (
    <Box
      sx={[
        {
          overflow: 'hidden',
          position: 'relative',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <CardMedia
        loop
        src={src}
        ref={videoRef}
        controls={false}
        component="video"
        onTouchStart={(event) => event.target.pause()}
        onContextMenu={(event) => event.preventDefault()}
        onTouchEnd={(event) => play && event.target.play()}
        onMouseEnter={(event) => handleMouseEnter(event.target)}
        onMouseLeave={(event) => handleMouseLeave(event.target)}
        {...slotProps?.video}
      />

      {!play && (
        <IconButton
          size="large"
          onClick={handlePlay}
          sx={(theme) => ({
            ...iconButtonStyles(theme),
            right: '50%',
            bottom: '50%',
            transform: 'translate(50%, 50%)',
          })}
        >
          <Iconify icon="solar:play-bold" width={28} />
        </IconButton>
      )}

      <IconButton
        size="small"
        onClick={handleMuted}
        sx={(theme) => ({
          ...iconButtonStyles(theme),
          right: { xs: 7, sm: 8 },
          bottom: { xs: 32, sm: 34 },
        })}
      >
        <Iconify
          icon={`solar:volume-${muted ? 'cross' : 'loud'}-bold`}
          sx={{
            opacity: muted ? 0.7 : 1,
            width: { xs: 25, sm: 20 },
            height: { xs: 25, sm: 20 },
            '&:hover': { opacity: 1 },
          }}
        />
      </IconButton>
    </Box>
  );
}
